import { useState, useEffect } from "react";
import { X, PlusCircle } from "lucide-react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import { ExpenseType } from "../../types/transactionHistory.types";
import toast from "react-hot-toast";

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clinicId?: string;
  isAdmin?: boolean;
}

export default function AddExpenseModal({
  isOpen,
  onClose,
  onSuccess,
  clinicId,
  isAdmin = false,
}: AddExpenseModalProps) {
  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
  const [selectedType, setSelectedType] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentMethod, setPaymentMethod] = useState<"Cash" | "Online">("Cash");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCreateHead, setShowCreateHead] = useState(false);
  const [newHeadName, setNewHeadName] = useState("");
  const [newHeadAccess, setNewHeadAccess] = useState<"Admin Only" | "Admin + Clinic">("Admin + Clinic");
  const [creatingHead, setCreatingHead] = useState(false);

  useEffect(() => {
    if (isOpen) fetchExpenseTypes();
  }, [isOpen]);

  const fetchExpenseTypes = async () => {
    const res = await TransactionHistoryService.getExpenseTypes({ pageSize: 100 });
    if (res.success && res.data) {
      // Clinic users should only see expense types where isClinicAccessible is true
      const filtered = isAdmin
        ? res.data
        : res.data.filter((t) => t.isClinicAccessible !== false);
      setExpenseTypes(filtered);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !amount || !expenseDate) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await TransactionHistoryService.createExpense({
        clinicId,
        expenseType: selectedType,
        amount: parseFloat(amount),
        paymentMethod,
        expenseDate,
        description: description || undefined,
      });
      if (res.success) {
        toast.success("Expense added successfully");
        onSuccess();
        resetForm();
        onClose();
      } else {
        toast.error(res.message || "Failed to create expense");
      }
    } catch {
      toast.error("Failed to create expense");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateHead = async () => {
    if (!newHeadName.trim()) {
      toast.error("Please enter an expense head name");
      return;
    }
    setCreatingHead(true);
    try {
      const res = await TransactionHistoryService.createExpenseType({
        name: newHeadName.trim(),
        isClinicAccessible: newHeadAccess === "Admin + Clinic",
      });
      if (res.success) {
        toast.success("Expense head created");
        setShowCreateHead(false);
        setNewHeadName("");
        await fetchExpenseTypes();
      } else {
        toast.error(res.message || "Failed to create expense head");
      }
    } catch {
      toast.error("Failed to create expense head");
    } finally {
      setCreatingHead(false);
    }
  };

  const resetForm = () => {
    setSelectedType("");
    setAmount("");
    setExpenseDate(new Date().toISOString().split("T")[0]);
    setPaymentMethod("Cash");
    setDescription("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-xl w-[440px] max-h-[90vh] overflow-y-auto relative animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-emerald-600" />
            <h3 className="text-base font-semibold text-gray-800">
              Add New Expense
              {isAdmin && clinicId && <span className="text-xs text-gray-400 ml-1">(Clinic)</span>}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Expense Head */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Expense Head <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 bg-white"
              >
                <option value="">Select or search...</option>
                {expenseTypes.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
              {isAdmin && (
                <button
                  onClick={() => setShowCreateHead(true)}
                  className="px-3 py-2 text-xs font-medium text-white bg-secondary rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
                >
                  + Create expense head
                </button>
              )}
            </div>
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50"
              />
            </div>
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Payment Mode</label>
            <div className="flex gap-2">
              {(["Cash", "Online"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setPaymentMethod(mode)}
                  className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors
                    ${paymentMethod === mode
                      ? "border-secondary bg-red-50 text-secondary"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Note <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              placeholder="Eg: Purchased lab supplies..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-xs font-medium text-white bg-secondary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Create Expense Head Sub-modal */}
      {showCreateHead && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-xl w-[360px] p-5 animate-slideUp">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-800">Create expense head</h4>
              <button onClick={() => setShowCreateHead(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Enter Expense Head Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Workplace manage repairs"
                  value={newHeadName}
                  onChange={(e) => setNewHeadName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Assign to</label>
                <div className="flex gap-2">
                  {(["Admin Only", "Admin + Clinic"] as const).map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setNewHeadAccess(opt)}
                      className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-colors
                        ${newHeadAccess === opt
                          ? "border-secondary bg-red-50 text-secondary"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowCreateHead(false)}
                className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateHead}
                disabled={creatingHead}
                className="px-4 py-2 text-xs font-medium text-white bg-secondary rounded-lg hover:opacity-90 disabled:opacity-60"
              >
                {creatingHead ? "Creating..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
